import { useMemo } from 'react';

import { DataTable, SimpleButton, type TableColumn, type Colors } from '@/src/ui';
import type { ExamAssignmentDTO } from '../models/examAttemptDTOs';
import { ReportHeaderStyle, ReportCellStyle } from '@/src/utils';

interface ExamsTableProps {
    data: ExamAssignmentDTO[];
    tableActionOnClick: (createNewAttempt: boolean, actionId: number) => void;
}

export default function ExamsTable(props: ExamsTableProps) {
    const { data, tableActionOnClick } = props;

    const columns = useMemo<TableColumn[]>(() => {
        return [
            {
                dataKey: 'title',
                header: 'Title',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'examType',
                header: 'Assessment Type',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'actions',
                header: 'Actions',
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ExamAssignmentDTO) => {
                    let label = '';
                    let onClick = () => {};
                    let color: Colors = 'primary';
                    let disabled = false;

                    const attempts = row.attempts || [];

                    const completedAttempts = attempts.filter(
                        (a) => a.submittedStatus === 3
                    ).length;
                    const activeAttempt = attempts.find((a) => a.submittedStatus !== 3);
                    if (activeAttempt && activeAttempt.isTeacherFreezeEnabled) {
                        // Teacher freeze enabled, cannot continue
                        label = 'Frozen by Teacher';
                        disabled = true;
                    } else if (attempts.length === 0) {
                        // No attempts exist for some reason, create one
                        label = 'Begin';
                        color = 'success';
                        onClick = () => tableActionOnClick(true, row.id);
                    } else if (completedAttempts >= row.numberOfAttemptsAllowed) {
                        // All available attempts used
                        label = 'Completed';
                        disabled = true;
                    } else if (activeAttempt) {
                        // Attempt not started or in progress
                        onClick = () => tableActionOnClick(false, activeAttempt!.id);

                        if (activeAttempt.submittedStatus === 1) {
                            color = 'success';
                            label = 'Begin';
                        } else if (activeAttempt.submittedStatus === 2) {
                            color = 'warning';
                            label = 'Resume';
                        }
                    } else {
                        // Last attempt was completed and re-attempt is available
                        label = 'Take Again';
                        color = 'warning';
                        onClick = () => tableActionOnClick(true, row.id);
                    }

                    return (
                        <SimpleButton disabled={disabled} onClick={onClick} color={color}>
                            {label}
                        </SimpleButton>
                    );
                },
            },
        ];
    }, [tableActionOnClick]);

    return (
        <DataTable
            data={data}
            columns={columns}
            tableName="Users"
            alternatingRowColor
            defaultSortColumn="title"
            defaultSortDirection="asc"
        />
    );
}
